import dayjs from "dayjs";
import { Pencil } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import type { ExchangeRate } from "~/types/exchangeRates";

interface ExchangeRateTableProps {
  rates: ExchangeRate[];
  isLoading: boolean;
  onEdit: (rate: ExchangeRate) => void;
}

export function ExchangeRateTable({
  rates,
  isLoading,
  onEdit,
}: ExchangeRateTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (rates.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No hay tipos de cambio registrados
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Fecha</TableHead>
          <TableHead className="text-right">USD/CLP</TableHead>
          <TableHead className="text-right">Acciones</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rates.map((rate, index) => (
          <TableRow
            key={rate.id}
            style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
          >
            <TableCell className="font-medium">
              {dayjs(rate.rate_date).format("DD/MM/YYYY")}
            </TableCell>
            <TableCell className="text-right font-mono">
              ${formatCurrency(rate.usd_clp)}
            </TableCell>
            <TableCell className="text-right">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(rate)}
                title="Editar"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
